import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { Loader2, KeyRound, Mail, Lock, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

const TOKEN_KEY = "dtsp_admin_token";

export default function AdminInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/invitations/${token}/info`);
        if (!cancel) setInfo(data);
      } catch (e) {
        if (!cancel) setFatal(e.response?.data?.detail || "Invitation invalide, expirée ou déjà utilisée.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Mot de passe : 8 caractères minimum."); return; }
    if (password !== confirm) { setErr("Les mots de passe ne correspondent pas."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/invitations/${token}/accept`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      navigate("/admin", { replace: true });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erreur lors de la création du compte.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400" data-testid="admin-invite-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Vérification de l'invitation…
      </div>
    );
  }

  if (fatal) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center" data-testid="admin-invite-invalid">
          <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2">Invitation invalide</h2>
          <p className="text-sm text-zinc-400 leading-relaxed">{fatal}</p>
          <a href="/" className="btn-ghost inline-block mt-5 px-4 py-2 rounded-lg text-sm">Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  const roleLabel = info?.role === "superadmin"
    ? { txt: "Superadmin", cls: "bg-cyan-400/20 text-cyan-300" }
    : { txt: "Assistant", cls: "bg-amber-400/20 text-amber-300" };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="glass rounded-2xl p-6 sm:p-8 max-w-md w-full" data-testid="admin-invite-form">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-cyan-400/10 border border-cyan-400/30">
            <ShieldCheck className="h-5 w-5 text-cyan-300" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Création de votre compte admin</h1>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Invité par <span className="text-zinc-300 font-mono">{info?.created_by || "—"}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5">
          <span className={`text-[10px] uppercase font-mono tracking-wider px-2 py-1 rounded ${roleLabel.cls}`} data-testid="admin-invite-role-badge">
            {roleLabel.txt}
          </span>
          <span className="text-[11px] text-zinc-500">
            Expire {info?.expires_at ? new Date(info.expires_at).toLocaleString() : "—"}
          </span>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-zinc-400 mb-1 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="input w-full" placeholder="vous@exemple.com"
              data-testid="admin-invite-email" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400 mb-1 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Mot de passe</span>
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              className="input w-full" placeholder="8 caractères minimum"
              data-testid="admin-invite-password" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400 mb-1 flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Confirmer le mot de passe</span>
            <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="input w-full"
              data-testid="admin-invite-confirm" />
          </label>

          {err && (
            <div className="text-xs text-red-300 bg-red-400/10 border border-red-400/30 px-3 py-2 rounded" data-testid="admin-invite-error">
              {err}
            </div>
          )}

          <button type="submit" disabled={submitting}
            className="btn-primary w-full py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2"
            data-testid="admin-invite-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {submitting ? "Création…" : "Créer mon compte"}
          </button>
        </form>
      </div>
    </div>
  );
}
